import { useCallback } from 'react';

import { useMatches, useSearchParams } from '@remix-run/react';

import useGlobalModals from '~/components/GlobalModals/hooks/useGlobalModals';
import { COMMUNITY_ROUTE_IDS } from '~/constants/community';
import type {
  CommunityMemberContentPage,
  CommunityMemberContentPageResource,
  CommunityPageResource,
} from '~/interfaces/community/contentPage';

import type { ContentUpgradePurchase } from '../_components/ContentUpgradeModal/useContentUpgradeHandoff';
import { useCommunityAccess } from './useCommunityAccess';
import { useCommunityMembership } from './useCommunityMembership';

interface ContentUpgradeTarget {
  purchase: ContentUpgradePurchase;
  tierIds: string[];
  resourceSlug?: string;
}

export interface ContentUpgradeGate {
  target: ContentUpgradeTarget | null;
  isGranted: (purchase: ContentUpgradePurchase) => boolean;
}

interface PageRouteData {
  page: CommunityMemberContentPage | null;
}

interface ItemRouteData {
  page: CommunityMemberContentPageResource['page'] | null;
  resource: CommunityPageResource | null;
}

export const useContentUpgradeGate = (): ContentUpgradeGate => {
  const matches = useMatches();
  const { currentModal } = useGlobalModals();
  const { isMember } = useCommunityMembership();
  const { isCommunityAccessible } = useCommunityAccess();
  const [searchParams] = useSearchParams();
  const routeData = (routeId: string) => matches.find(({ id }) => id === routeId)?.data;
  const contentPage = (routeData(COMMUNITY_ROUTE_IDS.contentPage) as PageRouteData | undefined)?.page;
  const itemData = routeData(COMMUNITY_ROUTE_IDS.contentPageResource) as ItemRouteData | undefined;
  const item = itemData?.resource;

  const isGranted = useCallback(
    ({ resourceId }: ContentUpgradePurchase) => {
      if (item) return item.access === 'entitled';
      if (!contentPage) return false;
      if (!resourceId) return !contentPage.locked;

      return contentPage.resources.find(({ id }) => id === resourceId)?.access === 'entitled';
    },
    [item, contentPage],
  );

  const resourceId = searchParams.get('resource_id');
  const page = itemData?.page ?? contentPage;
  const resource = item ?? (resourceId ? contentPage?.resources.find(({ id }) => id === resourceId) : undefined);
  if (
    !isMember ||
    !isCommunityAccessible ||
    currentModal !== 'community_content_upgrade' ||
    !page ||
    (resourceId && !resource)
  ) {
    return { target: null, isGranted };
  }

  const purchase = { name: resource?.title ?? page.name, pageSlug: page.slug, resourceId: resource?.id };
  if (isGranted(purchase)) return { target: null, isGranted };

  return {
    target: {
      purchase,
      tierIds: resource ? resource.unlock_tier_ids : page.premium_tier_ids,
      resourceSlug: resource?.slug,
    },
    isGranted,
  };
};
